import { type BrandTokens, brandColor, brandTextStyle } from "../brand/tokens";
import { closestWeight } from "../scene/fonts";
import { createId } from "../scene/ops";
import {
  type CaptionAnimation,
  type CaptionLayout,
  type CaptionStyle,
  type CaptionsElement,
  captionLayoutSchema,
  captionStyleSchema,
} from "../scene/schema";
import { defaultCaptionBox, frameScale } from "./frame";

/** Ready-made caption looks (style + animation + layout). */
export interface CaptionPreset {
  id: string;
  label: string;
  description: string;
  style: Partial<CaptionStyle>;
  animation: { preset: CaptionAnimation; duration: number };
  layout?: Partial<CaptionLayout>;
}

const SHADOW = { x: 0, y: 4, blur: 16, color: "#00000080" };

export const CAPTION_PRESETS: readonly CaptionPreset[] = [
  {
    id: "pop",
    label: "Pop",
    description: "Wörter erscheinen beim Sprechen, das aktuelle ist farbig",
    style: {
      fontFamily: "Montserrat",
      fontWeight: 800,
      fontSize: 78,
      uppercase: false,
      color: "#ffffff",
      stroke: { color: "#000000", width: 5 },
      shadow: SHADOW,
      highlight: "color",
      highlightColor: "#facc15",
      activeScale: 1.06,
    },
    animation: { preset: "pop", duration: 180 },
    layout: { maxWordsPerLine: 3, maxLines: 2, maxCharsPerLine: 18 },
  },
  {
    id: "karaoke",
    label: "Karaoke",
    description: "Ganze Zeile sichtbar, gesprochene Wörter leuchten auf",
    style: {
      fontFamily: "Poppins",
      fontWeight: 700,
      fontSize: 70,
      color: "#ffffff",
      stroke: { color: "#000000", width: 4 },
      shadow: SHADOW,
      highlight: "color",
      highlightColor: "#22d3ee",
      upcomingOpacity: 0.55,
    },
    animation: { preset: "karaoke", duration: 150 },
    layout: { maxWordsPerLine: 4, maxLines: 2, maxCharsPerLine: 22 },
  },
  {
    id: "bold",
    label: "Kräftig",
    description: "Großbuchstaben, dicke Kontur, aktives Wort größer",
    style: {
      fontFamily: "Montserrat",
      fontWeight: 900,
      fontSize: 88,
      uppercase: true,
      color: "#ffffff",
      stroke: { color: "#000000", width: 8 },
      shadow: { x: 0, y: 6, blur: 0, color: "#000000cc" },
      highlight: "color",
      highlightColor: "#a3e635",
      activeScale: 1.12,
      letterSpacing: -0.01,
    },
    animation: { preset: "pop", duration: 160 },
    layout: { maxWordsPerLine: 3, maxLines: 1, maxCharsPerLine: 16 },
  },
  {
    id: "box",
    label: "Box",
    description: "Weiße Schrift auf dunkler Zeilenbox, blendet weich ein",
    style: {
      fontFamily: "Inter",
      fontWeight: 700,
      fontSize: 62,
      color: "#ffffff",
      background: "line",
      backgroundColor: "#000000b3",
      backgroundRadius: 14,
      highlight: "none",
    },
    animation: { preset: "fade", duration: 200 },
    layout: { maxWordsPerLine: 5, maxLines: 2, maxCharsPerLine: 26 },
  },
  {
    id: "pill",
    label: "Pille",
    description: "Das gesprochene Wort bekommt eine farbige Pille",
    style: {
      fontFamily: "Poppins",
      fontWeight: 800,
      fontSize: 72,
      color: "#ffffff",
      stroke: { color: "#000000", width: 3 },
      shadow: SHADOW,
      highlight: "box",
      highlightColor: "#8b5cf6",
      highlightTextColor: "#ffffff",
      backgroundRadius: 18,
    },
    animation: { preset: "bounce", duration: 200 },
    layout: { maxWordsPerLine: 3, maxLines: 2, maxCharsPerLine: 18 },
  },
  {
    id: "word",
    label: "Wort für Wort",
    description: "Immer nur das gesprochene Wort, groß in der Mitte",
    style: {
      fontFamily: "Archivo Black",
      fontWeight: 400,
      fontSize: 110,
      uppercase: true,
      color: "#ffffff",
      stroke: { color: "#000000", width: 8 },
      shadow: SHADOW,
      highlight: "none",
    },
    animation: { preset: "word-by-word", duration: 140 },
    layout: { maxWordsPerLine: 1, maxLines: 1, maxCharsPerLine: 20 },
  },
  {
    id: "typewriter",
    label: "Schreibmaschine",
    description: "Buchstaben erscheinen synchron zur Sprache",
    style: {
      fontFamily: "Space Grotesk",
      fontWeight: 700,
      fontSize: 64,
      color: "#ffffff",
      background: "page",
      backgroundColor: "#111111d9",
      backgroundRadius: 12,
      highlight: "none",
    },
    animation: { preset: "typewriter", duration: 120 },
    layout: { maxWordsPerLine: 5, maxLines: 2, maxCharsPerLine: 24 },
  },
  {
    id: "minimal",
    label: "Dezent",
    description: "Schlicht mit weichem Schatten, gleitet von unten ein",
    style: {
      fontFamily: "Inter",
      fontWeight: 600,
      fontSize: 56,
      color: "#ffffff",
      shadow: { x: 0, y: 2, blur: 12, color: "#000000b3" },
      highlight: "none",
    },
    animation: { preset: "slide-up", duration: 220 },
    layout: { maxWordsPerLine: 6, maxLines: 2, maxCharsPerLine: 30 },
  },
];

export function getCaptionPreset(id: string): CaptionPreset | undefined {
  return CAPTION_PRESETS.find((preset) => preset.id === id);
}

/**
 * Preset in the brand: font of the "heading-1" style, highlight in the accent
 * (or primary) colour. Null without a kit.
 */
export function brandCaptionPreset(tokens: BrandTokens | null | undefined): CaptionPreset | null {
  if (!tokens) return null;
  const heading = brandTextStyle(tokens, "heading-1");
  const accent = brandColor(tokens, "accent") ?? brandColor(tokens, "primary");
  if (!heading || !accent) return null;
  return {
    id: "brand",
    label: "Marke",
    description: "Schrift und Akzentfarbe aus dem Brand Kit",
    style: {
      fontFamily: heading.fontFamily,
      fontWeight: closestWeight(heading.fontFamily, Math.max(700, heading.fontWeight)),
      italic: heading.italic,
      letterSpacing: heading.letterSpacing,
      uppercase: heading.uppercase,
      fontSize: 76,
      color: "#ffffff",
      stroke: { color: "#000000", width: 5 },
      shadow: SHADOW,
      highlight: "color",
      highlightColor: accent.color,
      activeScale: 1.06,
    },
    animation: { preset: "pop", duration: 180 },
    layout: { maxWordsPerLine: 3, maxLines: 2, maxCharsPerLine: 18 },
  };
}

/** Applies a preset to a captions element (keeps position and size). */
export function applyCaptionPreset(element: CaptionsElement, preset: CaptionPreset): void {
  element.style = captionStyleSchema.parse({ ...preset.style });
  element.captionAnimation = { ...preset.animation };
  element.layout = captionLayoutSchema.parse({ ...element.layout, ...preset.layout });
  element.preset = preset.id;
  // The look now comes from the preset; brand links only if the preset is the brand's.
  if (element.brand && preset.id !== "brand") delete element.brand;
}

/**
 * Scales the sizes of a caption style (font, outline, shadow, box radius),
 * e.g. for a frame whose shorter side is not 1080 px.
 */
export function scaleCaptionStyle(style: CaptionStyle, factor: number): void {
  if (!Number.isFinite(factor) || factor <= 0 || Math.abs(factor - 1) < 1e-6) return;
  const round = (value: number) => Math.round(value * 10) / 10;
  style.fontSize = Math.min(500, Math.max(1, round(style.fontSize * factor)));
  style.backgroundRadius = Math.min(200, round(style.backgroundRadius * factor));
  if (style.stroke) style.stroke.width = Math.min(40, round(style.stroke.width * factor));
  if (style.shadow) {
    style.shadow.x = round(style.shadow.x * factor);
    style.shadow.y = round(style.shadow.y * factor);
    style.shadow.blur = Math.min(500, round(style.shadow.blur * factor));
  }
}

/** New captions element where the format expects captions (see `defaultCaptionBox`). */
export function createCaptionsElement(
  frame: { width: number; height: number },
  preset: CaptionPreset = CAPTION_PRESETS[0]!,
): CaptionsElement {
  const box = defaultCaptionBox(frame);
  const element: CaptionsElement = {
    id: createId("captions"),
    type: "captions",
    name: "Untertitel",
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    rotation: 0,
    opacity: 1,
    locked: false,
    hidden: false,
    style: captionStyleSchema.parse({}),
    captionAnimation: { preset: "pop", duration: 180 },
    layout: captionLayoutSchema.parse({}),
  };
  applyCaptionPreset(element, preset);
  // Preset sizes refer to a shorter side of 1080 px.
  scaleCaptionStyle(element.style, frameScale(frame));
  return element;
}
